import {
  fetchAnalyticsTimeSeries,
  buildSyntheticSeries,
  extractSelectedModels,
  extractMetricFromAntennaData,
  parseSeriesDate,
  windowSeriesByRange,
  getActiveBrand,
  type TimeSeriesPoint,
  type SyntheticSeriesPoint,
} from "./analytics-overview-api";
import { fetchMonitoringPerceptionSeries } from "./monitoring-perception-series-api";
import { holdLastCaptured } from "./series-helpers";

export type TimeRange = "baseline" | "full";

export interface PerceptionDataPoint {
  date: string;
  timestamp: number;
  [modelKey: string]: number | string;
}

export interface PerceptionDataResponse {
  data: PerceptionDataPoint[];
  events: any[];
  annotations: any[];
  selectedModels: string[];
  isSynthetic?: boolean;
}

function toDataPoint(p: TimeSeriesPoint | SyntheticSeriesPoint, idx: number, models: string[]): PerceptionDataPoint {
  const base = (p as any).overall || 0;
  const parsed = parseSeriesDate(p.date);
  const result: any = { date: p.date, timestamp: Number.isNaN(parsed) ? idx : parsed };
  models.forEach(k => { result[k] = (p as any)[k] ?? base; });
  result.overall = base;          // composite, for the headline (not a plotted line)
  return result;
}

export async function fetchPerceptionData(
  timeRange: TimeRange,
  antennaData?: any,
  selectedModels?: string[],
): Promise<PerceptionDataResponse> {
  // Canonical Upmode AI Index = the same composite Brand Monitoring shows
  // (monitoring overallScore). We anchor only the COMPOSITE (overall) of the
  // latest point to that value so the headline agrees with Brand Monitoring.
  // We intentionally do NOT flatten the per-model values to the composite, which
  // collapsed all four model lines onto one identical value. Per-model lines
  // keep their real, distinct readings.
  const canonical = extractMetricFromAntennaData(antennaData, "perception");
  const anchorLatest = (points: PerceptionDataPoint[], _models: string[]): PerceptionDataPoint[] => {
    if (!canonical || canonical.overall <= 0 || points.length === 0) return points;
    const last: PerceptionDataPoint = { ...points[points.length - 1] };
    (last as any).overall = canonical.overall;
    points[points.length - 1] = last;
    return points;
  };

  // Primary source: every Brand Monitoring run for this brand, so the AI Index
  // trends over all runs (the analytics_snapshots table collapses same-day runs
  // into one row, which left this chart showing only the latest run).
  try {
    const ms = await fetchMonitoringPerceptionSeries(getActiveBrand());
    if (ms.runCount >= 1) {
      const { series, models } = holdLastCaptured(ms.series, ms.selectedModels);
      const windowed = windowSeriesByRange(series, timeRange);
      return {
        data:           anchorLatest(windowed.map((p) => ({ ...p })) as PerceptionDataPoint[], models),
        events:         [],
        annotations:    [],
        selectedModels: models,
        isSynthetic:    false,
      };
    }
  } catch { /* fall through to snapshot/antenna paths */ }

  try {
    // Fallback: analytics_snapshots time-series. Always fetch the full 90d window
    // and slice client-side (below) so every chart + the overview share ONE
    // de-dup key (brand|90). This keeps the write-on-read /analytics/time-series
    // endpoint to a single hit per load and zero extra hits when the time-range
    // buttons are clicked.
    const ts = await fetchAnalyticsTimeSeries(undefined, 90);
    const windowed = windowSeriesByRange(ts.perception_series, timeRange);
    if (windowed.length > 0) {
      return {
        data:           anchorLatest(windowed.map((p, i) => toDataPoint(p, i, ts.selected_models)), ts.selected_models),
        events:         [],
        annotations:    [],
        selectedModels: ts.selected_models,
        isSynthetic:    false,
      };
    }
  } catch { /* fall through to antenna fallback */ }

  const models    = extractSelectedModels(antennaData, selectedModels);
  const synthetic = buildSyntheticSeries(antennaData, "perception", models);
  return {
    data:           anchorLatest(synthetic.map((p, i) => toDataPoint(p, i, models)), models),
    events:         [],
    annotations:    [],
    selectedModels: models,
    isSynthetic:    true,
  };
}